import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { fadeUp, stagger } from '@/lib/motion';
import { industries as industryList, extraKpis } from '@/lib/industryData';
import IndustrySelector from '@/components/shared/IndustrySelector';
import { ArrowRight, MessageCircle, Briefcase, Activity, Server, DollarSign, Users, ShieldCheck, Clock, Zap, TrendingDown, TrendingUp, Download, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Mock data per tab
const opsData = {
  kpis: [
    { label: 'Uptime', value: 99.7, suffix: '%', icon: Activity, color: '#10B981' },
    { label: 'Incidentes abiertos', value: 7, suffix: '', icon: AlertTriangle, color: '#EF4444' },
    { label: 'MTTR (horas)', value: 2.4, suffix: 'h', icon: Clock, color: '#F59E0B' },
  ],
  chart1: Array.from({ length: 7 }, (_, i) => ({ cat: ['Hardware', 'Red', 'Software', 'Energía', 'Integración', 'Otro', 'Seguridad'][i], count: [12, 8, 6, 3, 5, 2, 1][i] })),
  chart2: Array.from({ length: 14 }, (_, i) => ({ day: `${i + 1}`, uptime: 98.5 + Math.random() * 1.5 })),
  actions: ['Programar mantenimiento preventivo en Zona Sur', 'Escalar incidente #4521 a nivel 2', 'Revisar firmware kioscos modelo SelfPOS70', 'Actualizar certificados SSL servidores Edge'],
};

const tiData = {
  kpis: [
    { label: 'Integraciones OK', value: 96.8, suffix: '%', icon: Server, color: '#0EA5E9' },
    { label: 'Latencia prom.', value: 142, suffix: 'ms', icon: Zap, color: '#10B981' },
    { label: 'Cambios desplegados', value: 23, suffix: '', icon: TrendingUp, color: '#8B5CF6' },
  ],
  chart1: Array.from({ length: 6 }, (_, i) => ({ system: ['POS', 'ERP', 'Pagos', 'Monitoreo', 'ESL', 'CRM'][i], errors: [3, 7, 2, 1, 4, 0][i] })),
  chart2: Array.from({ length: 5 }, (_, i) => ({ sev: ['Crítico', 'Alto', 'Medio', 'Bajo', 'Info'][i], count: [1, 3, 8, 12, 5][i] })),
  alerts: ['Error de sync POS-ERP en sucursal Maipú #12', 'Latencia elevada gateway ESL zona RM', 'Certificado TLS próximo a expirar (7 días)', 'API de pagos retornando timeout intermitente'],
};

const finData = {
  kpis: [
    { label: 'CAPEX evitado (OPEX)', value: 45.2, suffix: 'M', prefix: '$', icon: TrendingDown, color: '#10B981' },
    { label: 'Ahorro estimado', value: 12.8, suffix: 'M', prefix: '$', icon: DollarSign, color: '#0EA5E9' },
    { label: 'ROI proyectado', value: 340, suffix: '%', icon: TrendingUp, color: '#8B5CF6' },
  ],
  chart1: Array.from({ length: 12 }, (_, i) => ({ mes: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][i], capex: 8 + Math.random() * 3, opex: 4 + Math.random() * 2 })),
  chart2: Array.from({ length: 6 }, (_, i) => ({ cat: ['HW falla', 'Downtime', 'Personal', 'Logística', 'Penalidad', 'Otro'][i], costo: [4.2, 3.1, 2.8, 1.5, 0.8, 0.5][i] })),
  savings: ['Migrar 30 cajas a arriendo reduce CAPEX en $18M anuales', 'SLA 24/7 evita pérdidas por downtime estimadas en $5M', 'Autoservicio reduce dotación de cajeros en un 25%', 'Monitoreo preventivo reduce incidentes críticos en 40%'],
};

const expData = {
  kpis: [
    { label: 'Tiempo de espera', value: 3.2, suffix: 'min', icon: Clock, color: '#F59E0B' },
    { label: 'Adopción autoservicio', value: 38, suffix: '%', icon: Users, color: '#0EA5E9' },
    { label: 'Satisfacción (demo)', value: 4.3, suffix: '/5', icon: ShieldCheck, color: '#10B981' },
  ],
  chart1: Array.from({ length: 8 }, (_, i) => ({ suc: `Suc ${i + 1}`, adopcion: 25 + Math.random() * 30 })),
  chart2: Array.from({ length: 12 }, (_, i) => ({ hora: `${8 + i}:00`, uso: Math.floor(20 + Math.sin(i / 2) * 40 + Math.random() * 15) })),
  critical: ['Sucursal 4: espera promedio sobre 6 min en horario punta', 'Sucursal 7: adopción autoservicio bajo 15% (capacitación)', 'Zona Norte: NPS bajo 3.5 en último trimestre', 'Kiosco #24 sin servicio por 48h (impacto CX)'],
};

const tabConfig = [
  { id: 'ops', label: 'Operaciones', icon: Activity, data: opsData, ctaText: 'Cotizar SLA 24/7', ctaHref: '/servicios/soporte-24-7' },
  { id: 'ti', label: 'TI', icon: Server, data: tiData, ctaText: 'Ver integraciones', ctaHref: '/software' },
  { id: 'fin', label: 'Finanzas', icon: DollarSign, data: finData, ctaText: 'Calcular TCO', ctaHref: '/servicios/arriendo-leasing' },
  { id: 'exp', label: 'Experiencia', icon: Users, data: expData, ctaText: 'Ver autoservicio', ctaHref: '/soluciones/autoservicio-self-checkout' },
];

const TabContent = ({ tab }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const d = tab.data;
  const listKey = d.actions ? 'actions' : d.alerts ? 'alerts' : d.savings ? 'savings' : 'critical';
  const listData = d[listKey] || [];
  const listTitle = listKey === 'actions' ? 'Acciones recomendadas' : listKey === 'alerts' ? 'Top alertas TI' : listKey === 'savings' ? 'Palancas de ahorro' : 'Puntos críticos de experiencia';

  return (
    <div ref={ref}>
      {/* KPIs */}
      <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={stagger} className="grid grid-cols-3 gap-4 mb-6">
        {d.kpis.map((kpi, i) => (
          <motion.div key={i} variants={fadeUp} className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 mb-2"><kpi.icon className="w-4 h-4" style={{ color: kpi.color }} /><span className="text-[11px] text-slate-500 font-medium">{kpi.label}</span></div>
            <div className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Syne' }}>
              {kpi.prefix || ''}{inView ? <CountUp end={kpi.value} decimals={kpi.suffix === '%' || kpi.suffix === 'h' || kpi.suffix === 'M' || kpi.suffix === 'min' || kpi.suffix === '/5' ? 1 : 0} duration={1.5} /> : '0'}<span className="text-base text-[#0EA5E9]">{kpi.suffix}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
          <h4 className="text-sm font-semibold mb-3">{tab.id === 'ops' ? 'Incidentes por categoría' : tab.id === 'ti' ? 'Errores por sistema' : tab.id === 'fin' ? 'CAPEX vs OPEX (mensual)' : 'Adopción por sucursal'}</h4>
          <ResponsiveContainer width="100%" height={200}>
            {tab.id === 'fin' ? (
              <LineChart data={d.chart1}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#94A3B8" /><YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} /><Line type="monotone" dataKey="capex" stroke="#EF4444" strokeWidth={2} dot={false} name="CAPEX" /><Line type="monotone" dataKey="opex" stroke="#10B981" strokeWidth={2} dot={false} name="OPEX" /><Legend wrapperStyle={{ fontSize: 11 }} /></LineChart>
            ) : (
              <BarChart data={d.chart1}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={Object.keys(d.chart1[0])[0]} tick={{ fontSize: 10 }} stroke="#94A3B8" /><YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} /><Bar dataKey={Object.keys(d.chart1[0])[1]} fill="#0EA5E9" radius={[4, 4, 0, 0]} /></BarChart>
            )}
          </ResponsiveContainer>
        </div>
        <div className="p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
          <h4 className="text-sm font-semibold mb-3">{tab.id === 'ops' ? 'Disponibilidad diaria' : tab.id === 'ti' ? 'Tickets por severidad' : tab.id === 'fin' ? 'Costo por tipo incidente' : 'Peak hours autoservicio'}</h4>
          <ResponsiveContainer width="100%" height={200}>
            {tab.id === 'ops' || tab.id === 'exp' ? (
              <AreaChart data={d.chart2}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={Object.keys(d.chart2[0])[0]} tick={{ fontSize: 10 }} stroke="#94A3B8" /><YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} /><Area type="monotone" dataKey={Object.keys(d.chart2[0])[1]} stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.1} strokeWidth={2} /></AreaChart>
            ) : (
              <BarChart data={d.chart2}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey={Object.keys(d.chart2[0])[0]} tick={{ fontSize: 10 }} stroke="#94A3B8" /><YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" /><Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} /><Bar dataKey={Object.keys(d.chart2[0])[1]} fill="#8B5CF6" radius={[4, 4, 0, 0]} /></BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Actions List + CTA */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-5 rounded-xl bg-white border border-slate-100 shadow-sm">
          <h4 className="text-sm font-semibold mb-3">{listTitle}</h4>
          <div className="space-y-2">
            {listData.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                <span className="w-5 h-5 rounded-full bg-[#0EA5E9]/10 text-[#0EA5E9] text-[10px] flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</span>
                <span className="text-xs text-slate-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-5 rounded-xl bg-[#0A2540] text-white flex flex-col justify-center">
          <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Syne' }}>¿Te interesa esta vista?</h4>
          <p className="text-xs text-slate-400 mb-4">Agenda una demo con datos reales de tu operación.</p>
          <Link to={tab.ctaHref} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg w-full">{tab.ctaText} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </div>
  );
};

export default function CSuitePage() {
  const [industry, setIndustry] = useState('retail');
  const ind = industryList.find(i => i.id === industry) || industryList[0];

  useEffect(() => {
    document.title = 'C-Suite Dashboard™ | ALLCOM — Operación, TI, Finanzas y experiencia';
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <div className="bg-[#050E1F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-400 bg-violet-400/10 border border-violet-400/20 rounded-full">DEMO · Datos simulados</span>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>C-Suite Dashboard<sup className="text-xs align-super ml-0.5 opacity-60">™</sup></h1>
              <p className="text-slate-400 mt-1 text-sm sm:text-base">Una vista por área para decidir rápido, con control y continuidad operativa.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/contacto" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#8B5CF6] rounded-xl hover:bg-violet-600 shadow-lg" data-testid="csuite-cta-demo">Agendar demo real <ArrowRight className="w-4 h-4" /></Link>
              <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Industry Selector */}
      <IndustrySelector selected={industry} onSelect={setIndustry} />

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center gap-3">
          <Link to="/" className="text-sm font-semibold text-[#0A2540] hover:text-[#8B5CF6]" style={{ fontFamily: 'Syne' }}>ALLCOM</Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-slate-500">C-Suite Dashboard™</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${ind.accent}15`, color: ind.accent }}>{ind.label}</span>
          <div className="ml-auto flex gap-2">
            <select className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white"><option>Últimos 30 días</option><option>7 días</option><option>90 días</option></select>
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50"><Download className="w-3.5 h-3.5" />Exportar</button>
          </div>
        </div>
      </div>

      {/* Dashboard with Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="csuite-dashboard">
        <Tabs defaultValue="ops" className="w-full">
          <TabsList className="flex flex-wrap gap-2 mb-8 bg-transparent">
            {tabConfig.map(t => (
              <TabsTrigger key={t.id} value={t.id} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl border border-slate-200 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white data-[state=active]:border-[#0A2540] hover:bg-slate-50" data-testid={`csuite-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabConfig.map(t => (
            <TabsContent key={t.id} value={t.id}><TabContent tab={t} /></TabsContent>
          ))}
        </Tabs>

        {/* Extra KPIs - Industry Specific */}
        <div className="mt-8 mb-6">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Métricas adicionales — {ind.label}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(extraKpis[industry] || extraKpis.retail).map((kpi, i) => (
              <div key={`${industry}-extra-${i}`} className="p-4 rounded-xl bg-white border border-slate-100 shadow-sm text-center">
                <div className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Syne' }}>{kpi.value}</div>
                <div className="text-[10px] text-slate-400 font-medium mt-1">{kpi.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Minimal footer CTA */}
        <div className="text-center py-6 border-t border-slate-100">
          <p className="text-xs text-slate-400 mb-3">¿Quieres ver C-Suite Dashboard™ con datos reales?</p>
          <div className="flex gap-3 justify-center">
            <Link to="/contacto" className="text-xs font-semibold px-4 py-2 rounded-lg text-white" style={{ backgroundColor: ind.accent }}>Agendar demo</Link>
            <a href="https://wa.me/56975294340" target="_blank" rel="noopener noreferrer" className="text-xs font-medium px-4 py-2 rounded-lg text-slate-500 border border-slate-200 hover:bg-slate-50">WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}
